import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
} from "@compass/core";

import { ListFooter, ReorderDataModels } from "components";
import {
  ContributorMatchServiceListDto,
  ContributorMatchServiceListResource,
  ListFilterBase,
  Permission,
  DataModelEnum,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { isAuthorized } from "services";
import { ContributorMatchServiceListPageHeader } from "./ContributorMatchServiceListPageHeader";
import { ContributorMatchServiceTable } from "./ContributorMatchServiceTable";

const routeName = "contributorMatchServices";

export const contributorMatchServiceListPageRoute: PageRoute = {
  handle: "Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ContributorMatchServiceListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<ContributorMatchServiceListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<ContributorMatchServiceListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<ContributorMatchServiceListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.contributorMatchServices.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.contributorMatchServices.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.contributorMatchServices.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page
        header={
          <ContributorMatchServiceListPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={contributorMatchServiceListPageRoute.handle}
          />
        }
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <ContributorMatchServiceTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              contributorMatchServices={listItems}
              setContributorMatchServices={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
              reorderDataModels={
                  isAuthorized(ROOT_PERMISSION, PermissionAction.Update) ? (
                    <ReorderDataModels
                      dataModelEnum={DataModelEnum.ContributorMatchService}
                      handle={contributorMatchServiceListPageRoute.handle}
                      getId={(item) => item.id}
                      getName={(item) => item.name}
                      getDisplayOrder={(item) => item.displayOrder}
                      fetch={() =>
                        ApiService.contributorMatchServices.getList({
                          page: PAGE_DEFAULT,
                          pageSize: PAGE_SIZE_UNLIMITED,
                        })
                      }
                    />
                  ) : undefined
                }
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
